import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'src/utils/use-navigate';
import { observer } from 'mobx-react-lite';
import { DisableReasons, VersionState } from 'src/utils/enums';
import { useStores } from 'src/components/store';
import { SystemConfiguration } from '../../../utils/system-configuration';

interface DisableReasonContentProps {
    disableReason?: DisableReasons | string;
    missingPermissions?: boolean;
    restore: () => void;
}

export const DisableReasonContent: React.FC<DisableReasonContentProps> = observer(({ missingPermissions,
                                                                                       disableReason,
                                                                                       restore }) => {
    const { fwVersionStore, logsStore } = useStores();
    const navigate = useNavigate();

    const goToDraftVersion = useCallback(() => {
        const pathToDraftVersion = fwVersionStore.pathToDraftVersion;
        if (pathToDraftVersion) {
            navigate(pathToDraftVersion);
        }
    }, [navigate, fwVersionStore.pathToDraftVersion]);

    const disableTooltipContent = useMemo(() => {
        if (missingPermissions) return 'You do not have permission to edit this item';
        switch (disableReason) {
            case DisableReasons.READ_ONLY_USER:
                return <>
                    <span>You are restricted to <span className="bold-text">view only.</span></span>
                    <span>Actions related to policy update are not available.</span>
                </>;
            case DisableReasons.DRAFT_EXIST:
                return <>
                    <span>A draft policy already exists</span>
                    <div className="tooltip-actions">
                        <button type="button" className="action-link bold-text"
                              onClick={() => goToDraftVersion()}>Edit draft</button>
                        {fwVersionStore.selectedFwVersion?.versionState === VersionState.INACTIVE && <>{' or '}
                            <button type="button" className="action-link bold-text" onClick={restore}>Restore</button></>}
                    </div>
                </>;
            case DisableReasons.INACTIVE_VERSION:
                return <>
                    <span>You can't modify an inactive policy version, you first need to restore this version.</span>
                    {fwVersionStore.selectedFwVersion && <button type="button" className="restore-button bold-text" onClick={restore}>Restore</button>}
                </>;
            case DisableReasons.PENDING_EXIST: return 'A pending policy already exists';
            case DisableReasons.DNS_PROTECTION_DEACTIVATED:
                return 'DNS Protection is deactivated';
            case DisableReasons.DEACTIVATE_VERSION: return 'It is not possible to modify a deactivated policy';
            case DisableReasons.SCHEDULED_VERSION_COME_ON:
                return `Can't edit a policy ${SystemConfiguration.getInstance().editApplyPolicyTimeBeforeSchedule} minutes before scheduling`;
            case DisableReasons.SCHEDULED_VERSION_EXITS : return 'Can\'t apply a policy when scheduling a policy';
            case DisableReasons.CAN_NOT_CHANGE: return 'This item cannot be changed';
            case DisableReasons.TCP_All_OR_UDP_All: return 'The service can\'t be set to Block';
            case DisableReasons.PUSH_NOT_AVAILABLE_PENDING: return 'Pushing pending policy is not allowed';
            case DisableReasons.PUSH_NOT_AVAILABLE_FROM_DEACTIVATED: return 'Pushing deactivated policy is not allowed';
            case DisableReasons.PUSH_NOT_AVAILABLE_BASE_VERSION: return 'Pushing a base policy is not allowed';
            case DisableReasons.RESTORE_NOT_AVAILABLE_FROM_DEACTIVATED: return 'Restoring deactivated policy is not allowed';
            case DisableReasons.PULL_NOT_AVAILABLE_PENDING: return 'Pulling policy while target connection has a pending policy is not allowed';
            case DisableReasons.PULL_NOT_AVAILABLE_BASE_VERSION: return 'Pulling policy while target connection has a base policy is not allowed';
            case DisableReasons.PULL_NOT_AVAILABLE_FROM_DEACTIVATED: return 'Pulling policy while target connection has a deactivated policy is not allowed';
            case DisableReasons.CREATE_DRAFT_NOT_AVAILABLE_DEACTIVATED: return 'Cannot create draft for connections with Dynamic Defense protection inactive';
            case DisableReasons.MAX_LOG_FOR_LOCAL_DOWNLOAD:
                return `Export disabled: Too many results. Please refine your search to fewer than ${logsStore.maxLogsForLocalDownload} results.`;
            case DisableReasons.SMART_DRAFT_IN_PROGRESS: return 'Policy actions are locked while smart protection draft creation is in progress.';

            default: return disableReason;
        }
    }, [disableReason, missingPermissions]);

    if (!disableReason && !missingPermissions) return null;
    return <div className="disable-content-tooltip">
        {disableTooltipContent}
    </div>;
});
